// The switch-based coupon collections, shared by the `/coupons` hub and each `/coupons/:collection`
// page so their titles, descriptions and filter mapping never drift apart.

export interface CouponCollectionDef {
  /** URL slug: /coupons/<slug>. */
  slug: string;
  /** The `/api/frontend/coupons` filter param that selects this collection. */
  filterParam: 'isVerified' | 'isExclusive' | 'freeShipping' | 'studentDiscount';
  /** Short label shown on the hub card badge. */
  badge: string;
  eyebrow: string;
  /** Short name — SEO title, breadcrumb, hub card. */
  title: string;
  /** Big punchy hero headline. */
  heroHeading: string;
  /** Noun used in the "{count} … live today" hero pill. */
  pillLabel: string;
  description: string;
}

export const COUPON_COLLECTIONS: CouponCollectionDef[] = [
  {
    slug: 'verified',
    filterParam: 'isVerified',
    badge: 'Verified',
    eyebrow: 'Tested & Working',
    title: 'Verified Coupons',
    heroHeading: 'Verified coupon codes, updated every hour.',
    pillLabel: 'verified codes',
    description:
      "Hand-tested deals from the stores you actually shop. Working codes you won't find broken — every one marked with the Verified badge.",
  },
  {
    slug: 'exclusive',
    filterParam: 'isExclusive',
    badge: 'Exclusive',
    eyebrow: 'Only on SavingArena',
    title: 'Exclusive Coupons',
    heroHeading: 'Exclusive codes, only on SavingArena.',
    pillLabel: 'exclusive codes',
    description:
      "Coupons you won't find anywhere else — negotiated exclusively for SavingArena shoppers and marked with the Exclusive badge.",
  },
  {
    slug: 'free-shipping',
    filterParam: 'freeShipping',
    badge: 'Free Shipping',
    eyebrow: 'No Delivery Fees',
    title: 'Free Shipping Coupons',
    heroHeading: 'Free shipping coupons, zero delivery fees.',
    pillLabel: 'free shipping deals',
    description:
      'Skip the delivery charge — coupons and deals that include free shipping, from the stores you actually shop.',
  },
  {
    slug: 'student-discount',
    filterParam: 'studentDiscount',
    badge: 'Student',
    eyebrow: 'For Students',
    title: 'Student Discount Coupons',
    heroHeading: 'Student discounts, verified and ready.',
    pillLabel: 'student offers',
    description:
      'Extra savings just for students — verified student discount codes and offers you can use today.',
  },
];

export const COUPON_COLLECTION_BY_SLUG: Record<string, CouponCollectionDef> = Object.fromEntries(
  COUPON_COLLECTIONS.map((collection) => [collection.slug, collection]),
);
